import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ClearIcon from "@mui/icons-material/Clear";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  ColumnFiltersState,
  Row,
  useReactTable,
  OnChangeFn,
} from "@tanstack/react-table";
import { SkuTableLoading } from "./SkuTableLoading";
import { SkuTableEmpty } from "./SkuTableEmpty";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { type SkuTableDataType } from "./skuTableUtils";
import { HighlightedText } from "./HighlightedText";

// Local storage key for rows per page preference
const ROWS_PER_PAGE_STORAGE_KEY = "sku-table-rows-per-page";

// Default rows per page options
const rowsPerPageOptions = [5, 10, 25, 50, 100];

// Get initial rows per page from local storage or use default
const getInitialRowsPerPage = (): number => {
  try {
    const storedValue = localStorage.getItem(ROWS_PER_PAGE_STORAGE_KEY);
    if (storedValue) {
      const parsedValue = parseInt(storedValue, 10);
      if (rowsPerPageOptions.includes(parsedValue)) {
        return parsedValue;
      }
    }
  } catch (e) {
    console.error("Error accessing localStorage:", e);
  }
  return 10; // Default rows per page
};

interface SkuTableProps {
  data: SkuTableDataType[];
  loading?: boolean;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  isPaginationServer?: boolean;
  totalCount?: number;
  onSearchChange?: (value: string) => void;
  globalFilter?: string;
}

export const SkuTable: React.FC<SkuTableProps> = ({
  data,
  loading,
  page: serverPage,
  rowsPerPage: serverRowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  isPaginationServer = false,
  totalCount = 0,
  onSearchChange,
  globalFilter: externalGlobalFilter,
}) => {
  // Pagination state - use local state if not using server-side pagination
  const [clientPage, setClientPage] = useState(0);
  const [clientRowsPerPage, setClientRowsPerPage] = useState(
    getInitialRowsPerPage
  );

  // Use either server-side pagination values or local state values
  const page = isPaginationServer ? serverPage || 0 : clientPage;
  const rowsPerPage = isPaginationServer
    ? serverRowsPerPage || 10
    : clientRowsPerPage;

  // Sorting state - initialize with skuCode sorted ascending by default, and modify the state
  // handler to always keep at least one sort active
  const [sorting, setSorting] = useState<SortingState>([
    { id: "skuName", desc: false },
  ]);

  // Custom handler for sorting changes that ensures at least one column is always sorted
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    updaterOrValue => {
      setSorting(prev => {
        // Get the next value, whether it's a function or direct value
        const nextValue =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev)
            : updaterOrValue;

        // If trying to clear all sorting, reapply default sort
        if (nextValue.length === 0) {
          return [{ id: "skuName", desc: false }];
        }

        // Otherwise use the provided value
        return nextValue;
      });
    },
    []
  );

  // Filtering state
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Use internal or external global filter depending on whether server-side pagination is used
  const [internalGlobalFilter, setInternalGlobalFilter] = useState("");
  const globalFilter = isPaginationServer
    ? externalGlobalFilter || ""
    : internalGlobalFilter;

  // Reset page when data changes or sorting/filtering is applied (only for client-side pagination)
  useEffect(() => {
    if (!isPaginationServer) {
      setClientPage(0);
    }
  }, [data, sorting, globalFilter, columnFilters, isPaginationServer]);

  // Save rows per page to local storage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(ROWS_PER_PAGE_STORAGE_KEY, rowsPerPage.toString());
    } catch (e) {
      console.error("Error saving to localStorage:", e);
    }
  }, [rowsPerPage]);

  // Handle global filter change
  const handleGlobalFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (isPaginationServer && onSearchChange) {
        // Use the callback for server-side search
        onSearchChange(newValue);
      } else {
        // Use local state for client-side search
        setInternalGlobalFilter(newValue);
      }
    },
    [isPaginationServer, onSearchChange]
  );

  // Clear global filter
  const handleClearGlobalFilter = useCallback(() => {
    if (isPaginationServer && onSearchChange) {
      // Use the callback for server-side search
      onSearchChange("");
    } else {
      // Use local state for client-side search
      setInternalGlobalFilter("");
    }
  }, [isPaginationServer, onSearchChange]);

  const { translate } = useFlightTranslation();
  const columnHelper = createColumnHelper<SkuTableDataType>();

  // Create a custom sort function that handles numeric values correctly
  const customSortingFn = (
    rowA: Row<any>,
    rowB: Row<any>,
    columnId: string
  ): number => {
    // Get values from both rows
    const valueA = rowA.getValue(columnId);
    const valueB = rowB.getValue(columnId);

    // Handle undefined/null values
    if (valueA == null) {
      return 1; // null values go to the end
    }
    if (valueB == null) {
      return -1; // null values go to the end
    }

    // Check if both values can be parsed as numbers
    const numA = Number(valueA);
    const numB = Number(valueB);

    // If both are valid numbers, compare them numerically
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }

    // Otherwise, fall back to string comparison
    return String(valueA).localeCompare(String(valueB));
  };

  // Function to determine if a field is numeric
  const isNumericField = useCallback((columnId: string) => {
    const numericFields = [
      "teamSizeMin",
      "teamSizeMax",
      "targetRate",
      "rate",
      "scale",
    ];
    return numericFields.includes(columnId);
  }, []);

  // Column definitions with sorting enabled
  const columns = useMemo(() => {
    return [
      columnHelper.accessor("stationName", {
        header: ({ column }) => {
          // Make header sortable
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  color: "primary.main",
                  "& .MuiSvgIcon-root": {
                    opacity: 1,
                  },
                },
              }}
              onClick={() => column.toggleSorting()}
            >
              {translate("Process Name")}
              {column.getIsSorted() === "asc" && (
                <ArrowUpwardIcon
                  fontSize="small"
                  sx={{ ml: 0.5, color: "primary.main" }}
                />
              )}
              {column.getIsSorted() === "desc" && (
                <ArrowDownwardIcon
                  fontSize="small"
                  sx={{ ml: 0.5, color: "primary.main" }}
                />
              )}
            </Box>
          );
        },
        cell: ({ row, getValue }) => {
          const value = getValue<string>();
          return (
            <HighlightedText text={value || "-"} highlight={globalFilter} />
          );
        },
      }),
      columnHelper.accessor("skuCode", {
        header: ({ column }) => {
          // Make header sortable
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  color: "primary.main",
                  "& .MuiSvgIcon-root": {
                    opacity: 1,
                  },
                },
              }}
              onClick={() => column.toggleSorting()}
            >
              {translate("SKU ID")}
              {column.getIsSorted() === "asc" && (
                <ArrowUpwardIcon
                  fontSize="small"
                  sx={{ ml: 0.5, color: "primary.main" }}
                />
              )}
              {column.getIsSorted() === "desc" && (
                <ArrowDownwardIcon
                  fontSize="small"
                  sx={{ ml: 0.5, color: "primary.main" }}
                />
              )}
            </Box>
          );
        },
        cell: ({ row, getValue }) => {
          const value = getValue<string>();
          return (
            <HighlightedText text={value || "-"} highlight={globalFilter} />
          );
        },
      }),
      columnHelper.accessor("skuName", {
        header: ({ column }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": {
                color: "primary.main",
                "& .MuiSvgIcon-root": {
                  opacity: 1,
                },
              },
            }}
            onClick={() => column.toggleSorting()}
          >
            {translate("SKU Description")}
            {column.getIsSorted() === "asc" && (
              <ArrowUpwardIcon
                fontSize="small"
                sx={{ ml: 0.5, color: "primary.main" }}
              />
            )}
            {column.getIsSorted() === "desc" && (
              <ArrowDownwardIcon
                fontSize="small"
                sx={{ ml: 0.5, color: "primary.main" }}
              />
            )}
          </Box>
        ),
        cell: ({ row, getValue }) => {
          const rowValue = row.original;
          const value = getValue<string>();

          // Show loading indicator when:
          // 1. The table is in a loading state OR
          // 2. The value is explicitly "N/A" (regardless of loading state)
          if (loading || value === "N/A") {
            return (
              <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>
                Loading...
              </Box>
            );
          }

          return (
            <HighlightedText
              text={value || rowValue.skuCode || "-"}
              highlight={globalFilter}
            />
          );
        },
      }),
      columnHelper.accessor("targetRate", {
        header: ({ column }) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  color: "primary.main",
                  "& .MuiSvgIcon-root": {
                    opacity: 1,
                  },
                },
              }}
              onClick={() => column.toggleSorting()}
            >
              {translate("Target Rate")}
              {column.getIsSorted() === "asc" && (
                <ArrowUpwardIcon
                  fontSize="small"
                  sx={{ ml: 0.5, color: "primary.main" }}
                />
              )}
              {column.getIsSorted() === "desc" && (
                <ArrowDownwardIcon
                  fontSize="small"
                  sx={{ ml: 0.5, color: "primary.main" }}
                />
              )}
            </Box>
            <Box
              sx={{
                fontSize: "0.7rem",
                color: "text.secondary",
                mt: 0.5,
                lineHeight: 1,
              }}
            >
              {translate("*For minimum team size")}
            </Box>
          </Box>
        ),
        cell: ({ row, getValue }) => {
          const rowValue = row.original;
          const targetRate = rowValue.rate || rowValue.targetRate;
          const timeWindow = rowValue.timeWindow;

          // Get station data for this SKU
          if (rowValue.isPartialData) {
            return (
              <Tooltip
                title={rowValue.errorMessage || "Error loading station data"}
              >
                <span style={{ color: "orange" }}>N/A</span>
              </Tooltip>
            );
          }

          // If we don't have a target rate
          if (targetRate === undefined || targetRate === null) {
            return "-";
          }

          // Format the time unit based on timeWindow
          let timeUnitDisplay = "";
          if (timeWindow !== undefined && timeWindow !== null) {
            // Handle string values that might be JSON strings like "3600000"
            const timeWindowNum = parseFloat(
              typeof timeWindow === "string"
                ? timeWindow
                : timeWindow.toString()
            );

            // Add debugging to see actual values
            console.log(
              "timeWindow value:",
              timeWindow,
              "parsed as:",
              timeWindowNum
            );

            if (!isNaN(timeWindowNum)) {
              // Check for common time units with approximate matching to handle potential floating point issues
              if (
                timeWindowNum === 3600000 ||
                Math.abs(timeWindowNum - 3600000) < 0.1
              ) {
                timeUnitDisplay = "hour";
              } else if (
                timeWindowNum === 60000 ||
                Math.abs(timeWindowNum - 60000) < 0.1
              ) {
                timeUnitDisplay = "minute";
              } else if (
                timeWindowNum === 1000 ||
                Math.abs(timeWindowNum - 1000) < 0.1
              ) {
                timeUnitDisplay = "second";
              } else if (Math.abs(timeWindowNum % 3600000) < 0.1) {
                const hours = Math.round(timeWindowNum / 3600000);
                timeUnitDisplay = `${hours} hour${hours !== 1 ? "s" : ""}`;
              } else if (Math.abs(timeWindowNum % 60000) < 0.1) {
                const minutes = Math.round(timeWindowNum / 60000);
                timeUnitDisplay = `${minutes} minute${minutes !== 1 ? "s" : ""}`;
              } else if (Math.abs(timeWindowNum % 1000) < 0.1) {
                const seconds = Math.round(timeWindowNum / 1000);
                timeUnitDisplay = `${seconds} second${seconds !== 1 ? "s" : ""}`;
              } else {
                // For raw millisecond values like "3600000", convert to friendly format
                if (timeWindowNum === 3600000) {
                  timeUnitDisplay = "hour";
                } else if (timeWindowNum === 60000) {
                  timeUnitDisplay = "minute";
                } else if (timeWindowNum === 1000) {
                  timeUnitDisplay = "second";
                } else {
                  timeUnitDisplay = `${timeWindowNum} ms`;
                }
              }
            }
          }

          // Try to directly get the timeWindow from the row.rate property if available
          // The main source is timeWindow but it's often undefined
          const displayTargetRate = targetRate?.toString() || "-";

          // Handle fields that don't have timeWindow but do have rate or targetRate
          // Check if the rate or targetRate includes a value like "960.0 / 3600000"
          const rateString = rowValue.rate?.toString() || displayTargetRate;
          if (rateString.includes(" / 3600000")) {
            return rateString.replace(" / 3600000", " / hour");
          } else if (rateString.includes(" / 60000")) {
            return rateString.replace(" / 60000", " / minute");
          } else if (rateString.includes(" / 1000")) {
            return rateString.replace(" / 1000", " / second");
          }

          // Direct check for common values
          const timeWindowStr = timeWindow?.toString() || "";
          if (timeWindow === 3600000 || timeWindowStr === "3600000") {
            return `${displayTargetRate} / hour`;
          } else if (timeWindow === 60000 || timeWindowStr === "60000") {
            return `${displayTargetRate} / minute`;
          } else if (timeWindow === 1000 || timeWindowStr === "1000") {
            return `${displayTargetRate} / second`;
          }

          // Format the combined display
          if (timeUnitDisplay) {
            return `${displayTargetRate} / ${timeUnitDisplay}`;
          } else {
            return displayTargetRate;
          }
        },
        sortingFn: customSortingFn,
        meta: {
          isNumeric: true, // Mark as numeric field
        },
        size: 150,
        enableSorting: true,
        // Add flex-grow: 0 to prevent column from expanding
        minSize: 100,
        maxSize: 150,
      }),
      columnHelper.accessor("scale", {
        header: ({ column }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": {
                color: "primary.main",
                "& .MuiSvgIcon-root": {
                  opacity: 1,
                },
              },
            }}
            onClick={() => column.toggleSorting()}
          >
            {translate("SKU Scale")}
            {column.getIsSorted() === "asc" && (
              <ArrowUpwardIcon
                fontSize="small"
                sx={{ ml: 0.5, color: "primary.main" }}
              />
            )}
            {column.getIsSorted() === "desc" && (
              <ArrowDownwardIcon
                fontSize="small"
                sx={{ ml: 0.5, color: "primary.main" }}
              />
            )}
          </Box>
        ),
        cell: ({ row, getValue }) => {
          const value = getValue<string>();

          // Show loading indicator when:
          // 1. The table is in a loading state OR
          // 2. The value is explicitly "N/A" (regardless of loading state)
          if (loading || value === "N/A") {
            return (
              <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>
                Loading...
              </Box>
            );
          }

          return (
            <HighlightedText text={value || "-"} highlight={globalFilter} />
          );
        },
        sortingFn: customSortingFn,
        meta: {
          isNumeric: true, // Mark as numeric field
        },
        size: 150,
        enableSorting: true,
        // Add flex-grow: 0 to prevent column from expanding
        minSize: 100,
        maxSize: 150,
      }),
      // Custom accessor for teamSizeMin (calculated from teamSizes)
      columnHelper.accessor(
        row => {
          const teamSizes = row.teamSizes;
          if (teamSizes && Array.isArray(teamSizes) && teamSizes.length > 0) {
            return Math.min(...teamSizes);
          }
          return null;
        },
        {
          id: "teamSizeMin", // Custom ID for the column
          header: ({ column }) => {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": {
                    color: "primary.main",
                    "& .MuiSvgIcon-root": {
                      opacity: 1,
                    },
                  },
                }}
                onClick={() => column.toggleSorting()}
              >
                {translate("Team Size Min")}
                {column.getIsSorted() === "asc" && (
                  <ArrowUpwardIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: "primary.main" }}
                  />
                )}
                {column.getIsSorted() === "desc" && (
                  <ArrowDownwardIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: "primary.main" }}
                  />
                )}
              </Box>
            );
          },
          cell: ({ row, getValue }) => {
            const value = getValue<number>();
            if (value !== null && value !== undefined) {
              return (
                <HighlightedText
                  text={value.toString()}
                  highlight={globalFilter}
                />
              );
            } else {
              return "-";
            }
          },
          sortingFn: customSortingFn,
          meta: {
            isNumeric: true, // Mark as numeric field
          },
          size: 150,
          enableSorting: true,
          // Add flex-grow: 0 to prevent column from expanding
          minSize: 100,
          maxSize: 150,
        }
      ),
      // Custom accessor for teamSizeMax (calculated from teamSizes)
      columnHelper.accessor(
        row => {
          const teamSizes = row.teamSizes;
          if (teamSizes && Array.isArray(teamSizes) && teamSizes.length > 0) {
            return Math.max(...teamSizes);
          }
          return null;
        },
        {
          id: "teamSizeMax", // Custom ID for the column
          header: ({ column }) => {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  "&:hover": {
                    color: "primary.main",
                    "& .MuiSvgIcon-root": {
                      opacity: 1,
                    },
                  },
                }}
                onClick={() => column.toggleSorting()}
              >
                {translate("Team Size Max")}
                {column.getIsSorted() === "asc" && (
                  <ArrowUpwardIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: "primary.main" }}
                  />
                )}
                {column.getIsSorted() === "desc" && (
                  <ArrowDownwardIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: "primary.main" }}
                  />
                )}
              </Box>
            );
          },
          cell: ({ row, getValue }) => {
            const value = getValue<number>();
            if (value !== null && value !== undefined) {
              return (
                <HighlightedText
                  text={value.toString()}
                  highlight={globalFilter}
                />
              );
            } else {
              return "-";
            }
          },
          sortingFn: customSortingFn,
          meta: {
            isNumeric: true, // Mark as numeric field
          },
          size: 150,
          enableSorting: true,
          // Add flex-grow: 0 to prevent column from expanding
          minSize: 100,
          maxSize: 150,
        }
      ),
      // Add Linear Scaling column
      columnHelper.accessor("isLinearScaling", {
        header: ({ column }) => {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  color: "primary.main",
                  "& .MuiSvgIcon-root": {
                    opacity: 1,
                  },
                },
              }}
              onClick={() => column.toggleSorting()}
            >
              {translate("Linear Scaling")}
              {column.getIsSorted() === "asc" && (
                <ArrowUpwardIcon
                  fontSize="small"
                  sx={{ ml: 0.5, color: "primary.main" }}
                />
              )}
              {column.getIsSorted() === "desc" && (
                <ArrowDownwardIcon
                  fontSize="small"
                  sx={{ ml: 0.5, color: "primary.main" }}
                />
              )}
            </Box>
          );
        },
        cell: ({ row, getValue }) => {
          const value = getValue<boolean>();
          // Return yes/no based on the boolean value
          return (
            <Box
              sx={{
                color: value ? "success.main" : "text.secondary",
                fontWeight: value ? "bold" : "normal",
              }}
            >
              {value ? "Yes" : "No"}
            </Box>
          );
        },
        // Ensure proper sorting for boolean values
        sortingFn: (rowA, rowB, columnId) => {
          const valueA = rowA.getValue(columnId) as boolean;
          const valueB = rowB.getValue(columnId) as boolean;

          // Convert to numbers for comparison
          const numA = valueA ? 1 : 0;
          const numB = valueB ? 1 : 0;

          return numA - numB;
        },
      }),
    ];
  }, [globalFilter, translate, columnHelper, loading]);

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    if (isPaginationServer) {
      if (onPageChange) {
        onPageChange(newPage);
      }
    } else {
      setClientPage(newPage);
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    if (isPaginationServer) {
      if (onRowsPerPageChange) {
        onRowsPerPageChange(newRowsPerPage);
      }
    } else {
      setClientRowsPerPage(newRowsPerPage);
      setClientPage(0);
    }
  };

  // We handle sorting, filtering, and pagination properly
  // This ensures sorting and filtering happen first, then we paginate the results

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      pagination: {
        pageIndex: page,
        pageSize: rowsPerPage,
      },
    },
    sortingFns: {
      customSort: customSortingFn,
    },
    onSortingChange: handleSortingChange, // Use custom handler
    onGlobalFilterChange: setInternalGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Only enable client-side filtering for non-server-side pagination
    getFilteredRowModel: isPaginationServer ? undefined : getFilteredRowModel(),
    getPaginationRowModel: isPaginationServer
      ? undefined
      : getPaginationRowModel(),
    // Custom global filter function using exact substring match across all columns
    globalFilterFn: (row, columnId, filterValue) => {
      const rowValues = Object.values(row.original)
        .filter(Boolean)
        .map(value => {
          if (Array.isArray(value)) {
            return value.join(" ");
          }
          return String(value).toLowerCase();
        })
        .join(" ");

      return rowValues.toLowerCase().includes(filterValue.toLowerCase());
    },
    debugTable: false,
  });

  // Enhanced pagination configuration
  const sortedAndFilteredRows = isPaginationServer
    ? data // For server-side, we just show the current data without additional filtering
    : table.getFilteredRowModel().rows;

  // For client-side pagination, the table's pagination model already gives us the correct rows for the current page
  // For server-side pagination, we need to use all rows since the server already gave us the correct subset
  const tableRows = table.getRowModel().rows;

  // Don't slice again when using client-side pagination, as the table already includes pagination
  // Only slice for server-side pagination if we somehow received too many rows from the server
  const dataRows = isPaginationServer
    ? tableRows
    : table.getPaginationRowModel().rows;

  // Common UI elements and styles for both search and table headers
  const renderSearchField = () => (
    <Box sx={{ p: 2, bgcolor: "#f9f9f9", borderBottom: "1px solid #e0e0e0" }}>
      <TextField
        fullWidth
        placeholder={translate("Search all columns...")}
        value={globalFilter}
        onChange={handleGlobalFilterChange}
        variant="outlined"
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (() => {
            if (globalFilter) {
              return (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearGlobalFilter} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              );
            }
            return null;
          })(),
        }}
      />
    </Box>
  );

  const renderTableHead = () => (
    <TableHead>
      {table.getHeaderGroups().map(headerGroup => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => (
            <TableCell
              key={header.id}
              onClick={
                header.column.getCanSort()
                  ? () => header.column.toggleSorting()
                  : undefined
              }
              sx={{
                padding: index === 0 ? "12px 12px 12px 24px" : "12px",
                textAlign: "left",
                borderBottom: "2px solid #ddd",
                backgroundColor: "#ebebeb",
                fontWeight: "bold",
                cursor: header.column.getCanSort() ? "pointer" : "default",
                "&:hover": header.column.getCanSort()
                  ? {
                      backgroundColor: "#dddddd",
                      color: "primary.main",
                    }
                  : {},
                // Make sure inner content doesn't interfere with clicks
                "& .MuiBox-root": {
                  pointerEvents: "none", // Disable pointer events only in header cells
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "left",
                  alignItems: "center",
                  pointerEvents: "none", // Ensure clicks go to parent
                }}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </Box>
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableHead>
  );

  // Priority order: Loading -> Empty Data -> Normal Data
  return (
    <Box>
      {/* Global Search - Always shown */}
      {renderSearchField()}

      <TableContainer component={Paper} elevation={0}>
        <Table>
          {/* Table Headers - Always shown */}
          {renderTableHead()}

          <TableBody>
            {loading ? (
              /* Loading State */
              <SkuTableLoading colSpan={columns.length} />
            ) : dataRows.length === 0 ? (
              /* Empty State - Now also checks if table rows are empty after filtering */
              <SkuTableEmpty colSpan={columns.length} />
            ) : (
              /* Data Rows */
              dataRows.map((row, index) => (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      sx={{
                        padding: index === 0 ? "12px 12px 12px 24px" : "12px",
                        borderBottom: "1px solid #eee",
                        textAlign: isNumericField(cell.column.id)
                          ? "right"
                          : "left",
                        verticalAlign: "middle",
                        userSelect: "text",
                        cursor: "text",
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination - Always shown */}
      <TablePagination
        component="div"
        count={isPaginationServer ? -1 : sortedAndFilteredRows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        sx={{ borderTop: "1px solid #e0e0e0" }}
      />
    </Box>
  );
};
